import React, { useCallback } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useNavigate, useParams } from "react-router-dom";
import { isNil } from "ramda";

import { AffinityForm, ConfirmationView, FormView } from "affinityForm";
import { translateErrorMessages, useDryRunMutation, useSubmitMutation } from "affinityForm/helpers";
import { Layout } from "affinityUI/structures/layout";
import { UserConfirmation, UserForm } from "components/views/userEdit/";
import { UserInfoCreateType } from "components/views/userEdit/types";
import { userValidationErrors } from "components/views/userEdit/userForm/messages";
import { userInfoValidationSchema } from "components/views/userEdit/userForm/validationSchema";
import { UpdateUser, useGetRolesQuery, useGetUserDetailsQuery } from "gql";
import { collectGarbage, composeUpdates, invalidateUsersList } from "gql/invalidations";

const varSetter = (user: UserInfoCreateType) => ({
  user,
});

export const EditUserPage: React.FC = () => {
  const { showBoundary: onError } = useErrorBoundary();
  const { id } = useParams<{ id: string }>();
  const { data: rolesData, loading: loadingRoles } = useGetRolesQuery({ onError });
  const { data: userData, loading: loadingUser } = useGetUserDetailsQuery({ onError, variables: { id: id as string } });
  const roles = rolesData?.roles;
  const user = userData?.user;
  const navigate = useNavigate();
  const update = composeUpdates([invalidateUsersList, collectGarbage]);

  const handleCancel = useCallback(() => {
    navigate(`/users`);
  }, [navigate]);

  const validateUser = useDryRunMutation(UpdateUser, {
    varSetter,
    dataGetter: (response) => response?.data?.updateUser,
    parseError: translateErrorMessages(userValidationErrors),
  });
  const editUser = useSubmitMutation(UpdateUser, {
    varSetter,
    dataGetter: (response) => response?.data?.updateUser,
    onSave: "/users",
    update,
  });

  return (
    <AffinityForm<UserInfoCreateType>
      asyncValidation={validateUser}
      validationSchema={userInfoValidationSchema}
      initialData={user}
      confirmHandler={editUser}
    >
      <FormView>
        <Layout title="Edit user details" loading={loadingRoles || loadingUser} backLink="/users" showBackBtn>
          {!isNil(roles) && <UserForm onCancel={handleCancel} roles={roles} />}
        </Layout>
      </FormView>
      <ConfirmationView>
        <Layout title="Confirm and update user details" backLink=".." showBackBtn>
          {!isNil(roles) && <UserConfirmation onCancel={handleCancel} roles={roles} />}
        </Layout>
      </ConfirmationView>
    </AffinityForm>
  );
};
